// BIP39 recovery phrase (docs/03 §2): the 32-byte identity seed encoded as 24
// English words. Restoring the phrase re-derives the identical keypair, so
// friends see no key-change warning.

import { entropyToMnemonic, mnemonicToEntropy, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import { SEED_LENGTH } from './keys.js';

export const PHRASE_WORD_COUNT = 24;

export function seedToPhrase(seed: Uint8Array): string {
  if (seed.length !== SEED_LENGTH) {
    throw new Error(`identity seed must be ${SEED_LENGTH} bytes`);
  }
  return entropyToMnemonic(seed, wordlist);
}

/**
 * Tolerates pasted input: case, extra whitespace/line breaks, and any
 * numbering or punctuation (a phrase copied as a numbered list — "1. word
 * 2. word…" — is a real paste shape). BIP39 words are pure a–z, so every
 * non-letter run is a separator.
 */
export function normalizePhrase(phrase: string): string {
  return phrase.toLowerCase().replace(/[^a-z]+/g, ' ').trim();
}

export function isValidPhrase(phrase: string): boolean {
  const normalized = normalizePhrase(phrase);
  return (
    normalized.split(' ').length === PHRASE_WORD_COUNT &&
    validateMnemonic(normalized, wordlist)
  );
}

/** Throws on an invalid phrase (bad word, wrong count, or checksum failure). */
export function phraseToSeed(phrase: string): Uint8Array {
  const normalized = normalizePhrase(phrase);
  const words = normalized.split(' ');
  if (words.length !== PHRASE_WORD_COUNT) {
    throw new Error(`recovery phrase must be ${PHRASE_WORD_COUNT} words, got ${words.length}`);
  }
  return mnemonicToEntropy(normalized, wordlist);
}

/** The BIP39 wordlist, for autocomplete in restore UI. */
export const PHRASE_WORDLIST: readonly string[] = wordlist;
